import { MouseEvent, type PointerEvent as ReactPointerEvent, useCallback } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { isGroupShape } from '@/lib/groups/group-kind'
import {
  isShapeGeometryGestureBlocked,
  isShapeLockedForUserEdit,
} from '@/lib/groups/element-edit-policy'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import { isShapeSelectable } from '../helpers/selection-scope'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { SelectionHistoryApi } from './use-selection-history'
import type { EditScopeRefsApi } from './use-edit-scope-refs'
import type { SelectionPresentationApi } from './use-selection-presentation'
import type { FeedbackPulsesApi } from './use-feedback-pulses'
import type { ShapeSelectionApi } from './use-shape-selection'
import type { ShapeGroupingApi } from './use-shape-grouping'
import type { GestureStartApi } from './use-gesture-start'
import type { ShapeHitTestingApi } from './use-shape-hit-testing'
import type { AltClickCycleApi } from './use-alt-click-cycle'

export function useShapeClickHandlers(
  ctx: ShapeManagerStateApi &
    SelectionHistoryApi &
    EditScopeRefsApi &
    SelectionPresentationApi &
    FeedbackPulsesApi &
    ShapeSelectionApi &
    ShapeGroupingApi &
    GestureStartApi &
    ShapeHitTestingApi &
    AltClickCycleApi
) {
  const {
    ctrlCycleRef,
    editScopeGroupIdRef,
    getCtrlCycleTargetAtPoint,
    getLocalPointFromEvent,
    handleAltClick,
    handleMoveStart,
    handleShiftAltClick,
    isSelectionEnabled,
    onShapeClick,
    onShapeContextMenu,
    onShapeDoubleClick,
    recomputePassiveGroupBounds,
    recordShapeSelectionHistory,
    resetAltClickCycle,
    selectedShapeIdsRef,
    setSelectedShapeIds,
    shapesById,
    toggleSelection,
    triggerFrozenGeometryBlockedPulse,
  } = ctx

  const handleShapeClickInternal = useCallback(
    (shape: ShapeData, event: ReactPointerEvent) => {
      if (!isSelectionEnabled || !isShapeSelectable(shape, editScopeGroupIdRef.current)) return
      if (!event.altKey) resetAltClickCycle()

      if (event.shiftKey && event.altKey) {
        handleShiftAltClick(event)
        return
      }

      if (event.ctrlKey || event.metaKey || event.shiftKey) {
        const point = getLocalPointFromEvent(event)
        const target = point ? getCtrlCycleTargetAtPoint(point) : shape

        if (target) {
          toggleSelection(target.id)
        }

        return
      }

      if (event.altKey && event.button === 0) {
        handleAltClick(event)
        return
      }

      ctrlCycleRef.current = null

      const wasAlreadySelected = selectedShapeIdsRef.current.includes(shape.id)

      {
        const before = [...selectedShapeIdsRef.current]
        const next = [shape.id]

        selectedShapeIdsRef.current = next
        setSelectedShapeIds(next)
        recordShapeSelectionHistory(before, next)
      }

      onShapeClick?.(shape.id)

      if (isShapeGeometryGestureBlocked(shape, shapesById)) {
        if (wasAlreadySelected) {
          triggerFrozenGeometryBlockedPulse(shape)
        }

        return
      }

      handleMoveStart(event, shape.id, true)
    },
    [
      getCtrlCycleTargetAtPoint,
      getLocalPointFromEvent,
      handleAltClick,
      handleMoveStart,
      handleShiftAltClick,
      onShapeClick,
      isSelectionEnabled,
      resetAltClickCycle,
      toggleSelection,
      recordShapeSelectionHistory,
      shapesById,
      triggerFrozenGeometryBlockedPulse,
      ctrlCycleRef,
      editScopeGroupIdRef,
      selectedShapeIdsRef,
      setSelectedShapeIds,
    ]
  )

  const handleShapeContextMenuInternal = useCallback(
    (shape: ShapeData, event: MouseEvent) => {
      event.preventDefault()
      if (!isSelectionEnabled || !isShapeSelectable(shape, editScopeGroupIdRef.current)) return

      const isAlreadySelected = selectedShapeIdsRef.current.includes(shape.id)
      let finalSelectedIds: string[]

      if (isAlreadySelected) {
        finalSelectedIds = selectedShapeIdsRef.current
      } else {
        const before = [...selectedShapeIdsRef.current]

        finalSelectedIds = [shape.id]
        selectedShapeIdsRef.current = finalSelectedIds
        setSelectedShapeIds(finalSelectedIds)
        recordShapeSelectionHistory(before, finalSelectedIds)
      }

      onShapeContextMenu?.(finalSelectedIds, event, {
        targetLocked: shape.locked === true,
        targetShapeId: shape.id,
      })
    },
    [
      isSelectionEnabled,
      onShapeContextMenu,
      recordShapeSelectionHistory,
      editScopeGroupIdRef,
      selectedShapeIdsRef,
      setSelectedShapeIds,
    ]
  )

  const handleShapeDoubleClickInternal = useCallback(
    (shape: ShapeData, event: MouseEvent) => {
      if (shape.locked === true) return
      if (isShapeGeometryGestureBlocked(shape, shapesById)) {
        if (
          shape.type === 'path' &&
          shape.path &&
          !isShapeTextFrame(shape) &&
          shape.textOnPath == null
        ) {
          // Frozen path: block node-edit entry and show feedback.
          //
          // Narrowed to paths that are NOT text frames (WMPCM-6980). A closed pen path carrying
          // `shapeTextParagraphs` is a legitimate chain member (`isLinkableShapeTextFrame`), and
          // double-clicking it means "edit the text", not "edit the nodes" — freeze must not stop
          // that. Letting it through costs nothing: `handleShapeDoubleClick` in center-canvas/hooks/use-shape-double-click.ts
          // routes a text frame to `enterShapeTextEdit` before it ever considers node edit, and
          // node-edit entry is independently guarded there by `canEnterPenPathNodeEdit`, which
          // raises FROZEN_NODE_EDIT_MESSAGE for exactly this case.
          //
          // `shape.textOnPath == null` carves out the same case for the same reason (WMPCM-7632):
          // double-clicking a path that already carries Text on a Path means "reopen the text",
          // which is a content edit freeze does not block. Without it this branch would pulse
          // "frozen" and return, leaving double-click the one route that refuses an object the
          // menus now accept. It cannot leak into node edit: the Text-on-a-Path branch in
          // `handleShapeDoubleClick` runs before the path branch and returns.
          triggerFrozenGeometryBlockedPulse(shape)
          return
        }

        // Locked (self or via ancestry): fully block — locked objects and their descendants
        // stay untouchable, including entering isolation mode on a locked group.
        // Frozen groups intentionally fall through: isolation-mode entry is navigation, not
        // an edit, and a frozen group's children stay guarded once inside isolation by the
        // frozen-ancestor checks in element-edit-policy.ts (move/resize/delete blocked,
        // content edits like fill/stroke/text still allowed). Frozen non-group non-path
        // shapes already fell through here for the same reason (text editing).
        if (isShapeLockedForUserEdit(shape, shapesById)) {
          return
        }
      }

      if (isGroupShape(shape)) {
        recomputePassiveGroupBounds(shape.id, { includeNested: true })
      }

      onShapeDoubleClick?.(shape, event)
    },
    [onShapeDoubleClick, recomputePassiveGroupBounds, shapesById, triggerFrozenGeometryBlockedPulse]
  )

  return {
    handleShapeClickInternal,
    handleShapeContextMenuInternal,
    handleShapeDoubleClickInternal,
  }
}

export type ShapeClickHandlersApi = ReturnType<typeof useShapeClickHandlers>
